package com.zq.webproject.ui.productdetail
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Report
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import coil3.compose.AsyncImage
import com.zq.webproject.model.ProductDetail
import com.zq.webproject.model.ProductDetailResponse
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import kotlinx.coroutines.launch

// 图片前缀（接口返回的是相对路径，需拼接）
private const val IMAGE_PREFIX = "https://image.aiotzq.com/"

private val repository = Repository()

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProductDetailScreen(
    productId: String,
    onBackClick: () -> Unit,
    onOrderCreated: (String) -> Unit,  // 订单创建成功后的回调，传递订单号
    onLoginRequired: () -> Unit = {},    // 新增：需要登录时的回调
    onShowResourceInfo: (Int) -> Unit = {}  // 新增：显示资源信息的回调
) {
    println("ProductDetailScreen - 渲染商品详情页面，商品ID: $productId") // 日志输出

    // 状态管理：加载中、商品详情、错误
    var isLoading by remember { mutableStateOf(true) }
    var productDetail by remember { mutableStateOf<ProductDetailResponse?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    val coroutineScope = rememberCoroutineScope()
    val scrollState = rememberScrollState()
    
    // 投诉对话框状态
    var showComplaintDialog by remember { mutableStateOf(false) }

    // 请求商品详情接口
    LaunchedEffect(productId) {
        println("ProductDetailScreen - 开始请求商品详情，商品ID: $productId") // 日志输出
        coroutineScope.launch {
            try {
                val response = repository.getProductDetail(productId)
                if (response.isSuccess) {
                    productDetail = response.getOrNull()
                    println("ProductDetailScreen - 商品详情请求成功: ${productDetail?.data?.name}") // 日志输出
                    if (productDetail?.code != 200) {
                        errorMessage = productDetail?.message ?: "商品详情获取失败"
                        println("ProductDetailScreen - 商品详情返回错误: $errorMessage") // 日志输出
                    }
                } else {
                    errorMessage = "请求失败：${response.exceptionOrNull()?.message}"
                    println("ProductDetailScreen - 请求失败: $errorMessage") // 日志输出
                }
            } catch (e: Exception) {
                errorMessage = "网络异常：${e.message}"
                println("ProductDetailScreen - 网络异常: ${e.message}") // 日志输出
                e.printStackTrace()
            } finally {
                isLoading = false
            }
        }
    }

    // 主布局
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(text = "商品详情") },
                navigationIcon = {
                    IconButton(onClick = {
                        println("ProductDetailScreen - 点击返回按钮") // 日志输出
                        onBackClick()
                    }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "返回"
                        )
                    }
                },
                actions = {
                    // 投诉按钮
                    IconButton(onClick = {
                        println("ProductDetailScreen - 点击投诉按钮") // 日志输出
                        showComplaintDialog = true
                    }) {
                        Icon(
                            imageVector = Icons.Filled.Report,
                            contentDescription = "投诉",
                            tint = Color.White
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF5F5F5))
        ) {
            when {
                isLoading -> {
                    // 加载中
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }
                errorMessage != null -> {
                    // 错误页面
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(text = errorMessage!!, color = Color.Red, fontSize = 16.sp)
                            Button(
                                onClick = {
                                    println("ProductDetailScreen - 点击重新加载") // 日志输出
                                    isLoading = true
                                    errorMessage = null
                                    coroutineScope.launch {
                                        try {
                                            val response = repository.getProductDetail(productId)
                                            if (response.isSuccess) {
                                                productDetail = response.getOrNull()
                                                if (productDetail?.code != 200) {
                                                    errorMessage = productDetail?.message ?: "商品详情获取失败"
                                                }
                                            } else {
                                                errorMessage = "请求失败：${response.exceptionOrNull()?.message}"
                                            }
                                        } catch (e: Exception) {
                                            errorMessage = "网络异常：${e.message}"
                                        } finally {
                                            isLoading = false
                                        }
                                    }
                                },
                                modifier = Modifier.padding(top = 16.dp)
                            ) {
                                Text(text = "重新加载")
                            }
                        }
                    }
                }
                productDetail?.data == null -> {
                    // 商品不存在
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(text = "商品不存在", fontSize = 18.sp, color = Color.Gray)
                    }
                }
                else -> {
                    val product = productDetail!!.data!!
                    println("ProductDetailScreen - 开始渲染商品详情内容: ${product.name}") // 日志输出

                    Box(modifier = Modifier.fillMaxSize()) {
                        // 商品详情内容
                        Column(modifier = Modifier.fillMaxSize().verticalScroll(scrollState)) {
                            // 1. 商品主图轮播（拼多多风格：主图+缩略图）
                            // 过滤掉null值的图片
                            val subImages = listOfNotNull(
                                product.image1?.let { IMAGE_PREFIX + it },
                                product.image2?.let { IMAGE_PREFIX + it },
                                product.image3?.let { IMAGE_PREFIX + it }
                            )
                            println("ProductDetailScreen - 商品图片列表：主图=${product.imageUrl}, 子图数量=${subImages.size}") // 日志输出

                            ProductImageCarousel(
                                mainImage = IMAGE_PREFIX + product.imageUrl,
                                subImages = subImages
                            )

                            // 2. 商品名称区
                            ProductInfoSection(product)

                            // 3. 销量/浏览量等数据区
                            ProductStatsSection(product)

                            // 4. 商品详情描述区
                            ProductDescriptionSection(product.description)

                            // 底部留出空间给悬浮按钮
                            Spacer(modifier = Modifier.height(80.dp))
                        }

                        // 5. 底部悬浮购买按钮
                        BottomActionButton(
                            productId = productId.toInt(),
                            onOrderCreated = onOrderCreated,
                            onBackClick = onBackClick,  // 传递返回回调
                            onLoginRequired = onLoginRequired,  // 传递登录回调
                            onShowResourceInfo = onShowResourceInfo  // 传递资源信息回调
                        )
                    }
                }
            }
        }
    }
    
    // 投诉对话框
    if (showComplaintDialog && productDetail?.data != null) {
        ComplaintDialog(
            productId = productId,
            productName = productDetail!!.data!!.name,
            productPrice = productDetail!!.data!!.price,
            onDismiss = { showComplaintDialog = false }
        )
    }
}


// 1. 商品主图轮播（拼多多风格：顶部大图+下方缩略图）
@Composable
fun ProductImageCarousel(mainImage: String, subImages: List<String>) {
    // 当前显示的图片索引
    var currentImageIndex by remember { mutableStateOf(0) }
    val allImages = listOf(mainImage) + subImages

    Column {
        // 主图
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(300.dp)
                .background(Color.White)
        ) {
            AsyncImage(
                model = allImages[currentImageIndex],
                contentDescription = "商品主图",
                modifier = Modifier.fillMaxSize(),
                alignment = Alignment.Center,
                onError = {
                    println("ProductDetailScreen - 商品图片加载失败: ${allImages[currentImageIndex]}") // 日志输出
                }
            )
            // 图片指示器
            Text(
                text = "${currentImageIndex + 1}/${allImages.size}",
                color = Color.White,
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(16.dp)
                    .background(Color.Black.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
                    .padding(4.dp)
            )
        }

        // 缩略图横向列表
        LazyRow(
            modifier = Modifier
                .fillMaxWidth()
                .height(80.dp)
                .background(Color.White)
                .padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            items(allImages.size) { index ->
                AsyncImage(
                    model = allImages[index],
                    contentDescription = "缩略图$index",
                    modifier = Modifier
                        .size(70.dp)
                        .background(if (index == currentImageIndex) Color(0xFFEEEEEE) else Color.White)
                        .clickable {
                            println("ProductDetailScreen - 点击缩略图: index=$index") // 日志输出
                            currentImageIndex = index
                        }
                )
            }
        }
    }
}

// 投诉对话框
@Composable
fun ComplaintDialog(
    productId: String,
    productName: String,
    productPrice: Double,
    onDismiss: () -> Unit
) {
    var complaintReason by remember { mutableStateOf("") }
    var isSubmitting by remember { mutableStateOf(false) }
    var submitSuccess by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    val coroutineScope = rememberCoroutineScope()
    
    // 获取当前用户信息（如果已登录）
    val token = TokenManager.getToken()
    val userName = TokenManager.getUsername()
    // 注意：这里没有userId，如果需要可以从TokenManager扩展获取

    Dialog(onDismissRequest = onDismiss) {
        Surface(
            shape = RoundedCornerShape(16.dp),
            color = Color.White
        ) {
            Column(
                modifier = Modifier
                    .padding(24.dp)
                    .width(320.dp)
            ) {
                // 标题
                Text(
                    text = "投诉商品",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 16.dp)
                )

                if (submitSuccess) {
                    // 提交成功提示
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = "✅ 投诉已提交",
                            fontSize = 18.sp,
                            fontWeight = FontWeight.Medium,
                            color = Color(0xFF4CAF50),
                            modifier = Modifier.padding(vertical = 16.dp)
                        )
                        Text(
                            text = "我们将尽快处理您的投诉",
                            fontSize = 14.sp,
                            color = Color.Gray,
                            modifier = Modifier.padding(bottom = 16.dp)
                        )
                        Button(
                            onClick = onDismiss,
                            modifier = Modifier.fillMaxWidth(),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Color(0xFF1E88E5)
                            )
                        ) {
                            Text(text = "关闭")
                        }
                    }
                } else {
                    // 输入框
                    TextField(
                        value = complaintReason,
                        onValueChange = { complaintReason = it },
                        placeholder = { Text("请详细描述您的投诉理由...") },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(150.dp),
                        maxLines = 5,
                        colors = TextFieldDefaults.colors(
                            focusedContainerColor = Color(0xFFF5F5F5),
                            unfocusedContainerColor = Color(0xFFF5F5F5),
                            disabledContainerColor = Color(0xFFF5F5F5)
                        )
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    // 错误提示
                    if (errorMessage != null) {
                        Text(
                            text = errorMessage!!,
                            color = Color.Red,
                            fontSize = 12.sp,
                            modifier = Modifier.padding(vertical = 8.dp)
                        )
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    // 按钮区
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        // 取消按钮
                        OutlinedButton(
                            onClick = onDismiss,
                            modifier = Modifier.weight(1f),
                            enabled = !isSubmitting
                        ) {
                            Text(text = "取消")
                        }

                        // 提交按钮
                        Button(
                            onClick = {
                                if (complaintReason.isBlank()) {
                                    errorMessage = "请输入投诉理由"
                                    return@Button
                                }

                                println("提交投诉: productId=$productId, reason=$complaintReason")
                                isSubmitting = true
                                errorMessage = null

                                coroutineScope.launch {
                                    try {
                                        println("ComplaintDialog - 开始提交投诉")
                                        println("ComplaintDialog - productId=$productId, name=$productName, price=$productPrice")
                                        println("ComplaintDialog - reason=$complaintReason, userName=$userName")
                                        
                                        // 调用后端 API 提交投诉
                                        val result = repository.submitComplaint(
                                            productId = productId,
                                            productName = productName,
                                            productPrice = productPrice,
                                            complaintReason = complaintReason,
                                            userId = null,  // 可选：如果需要传递userId，需要扩展TokenManager
                                            userName = userName
                                        )
                                        
                                        // 检查返回结果
                                        if (result.isSuccess) {
                                            val response = result.getOrNull()
                                            if (response?.code == 200) {
                                                println("ComplaintDialog - 投诉提交成功: ${response.message}")
                                                submitSuccess = true
                                            } else {
                                                errorMessage = response?.message ?: "投诉提交失败"
                                                println("ComplaintDialog - 投诉提交失败: $errorMessage")
                                            }
                                        } else {
                                            val exception = result.exceptionOrNull()
                                            errorMessage = "提交失败: ${exception?.message ?: "未知错误"}"
                                            println("ComplaintDialog - 投诉提交异常: ${exception?.message}")
                                        }
                                    } catch (e: Exception) {
                                        errorMessage = "提交失败: ${e.message}"
                                        println("ComplaintDialog - 投诉提交失败: ${e.message}")
                                        e.printStackTrace()
                                    } finally {
                                        isSubmitting = false
                                    }
                                }
                            },
                            modifier = Modifier.weight(1f),
                            enabled = !isSubmitting,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Color(0xFF1E88E5)
                            )
                        ) {
                            if (isSubmitting) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(16.dp),
                                    color = Color.White,
                                    strokeWidth = 2.dp
                                )
                            } else {
                                Text(text = "提交")
                            }
                        }
                    }
                }
            }
        }
    }
}

// 2. 商品名称区
@Composable
fun ProductInfoSection(product: ProductDetail) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White)
            .padding(16.dp)
            .padding(bottom = 8.dp)
    ) {
        // 商品名称（多行省略）
        Text(
            text = product.name,
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )
    }
}


// 3. 销量/浏览量等数据区
@Composable
fun ProductStatsSection(product: ProductDetail) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White)
            .padding(16.dp)
            .padding(top = 0.dp),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(text = "已售", fontSize = 12.sp, color = Color.Gray)
            Text(text = product.sales.toString(), fontSize = 14.sp, fontWeight = FontWeight.Medium)
        }
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(text = "浏览", fontSize = 12.sp, color = Color.Gray)
            Text(text = product.views.toString(), fontSize = 14.sp, fontWeight = FontWeight.Medium)
        }
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(text = "分类", fontSize = 12.sp, color = Color.Gray)
            Text(text = product.category, fontSize = 14.sp, fontWeight = FontWeight.Medium)
        }
    }
    // 分割线
    Divider(modifier = Modifier.fillMaxWidth(), color = Color(0xFFEEEEEE))
}


// 4. 商品详情描述区
@Composable
fun ProductDescriptionSection(description: String) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White)
            .padding(16.dp)
            .padding(top = 8.dp)
    ) {
        Text(
            text = "商品详情",
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
            modifier = Modifier.padding(bottom = 8.dp)
        )
        Text(
            text = description,
            fontSize = 14.sp,
            lineHeight = 22.sp,
            color = Color(0xFF333333)
        )
    }
}


// 5. 底部悬浮购买按钮
@Composable
fun BoxScope.BottomActionButton(
    productId: Int,
    onOrderCreated: (String) -> Unit,
    onBackClick: () -> Unit,  // 添加返回首页的回调
    onLoginRequired: () -> Unit = {},  // 新增：需要登录时的回调
    onShowResourceInfo: (Int) -> Unit = {}  // 新增：显示资源信息的回调
) {
    val coroutineScope = rememberCoroutineScope()
    var isCreatingOrder by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    // 获取Token
    val token = TokenManager.getToken()

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .align(Alignment.BottomCenter)
    ) {
        // 错误提示
        if (errorMessage != null) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFFFEBEE))
                    .padding(12.dp)
            ) {
                Text(
                    text = errorMessage!!,
                    color = Color(0xFFD32F2F),
                    fontSize = 14.sp
                )
            }
        }

        // 按钮区域 - 只保留一个资源按钮
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(60.dp)
                .background(Color.White)
                .padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            // 查看资源信息按钮（执行支付检查逻辑）
            Button(
                onClick = {
                    println("点击查看资源信息按钮: productId=$productId") // 日志输出
                    
                    // 检查是否登录
                    if (token.isNullOrEmpty()) {
                        println("未登录，返回首页并打开登录弹框") // 日志输出
                        onLoginRequired()  // 调用回调，返回首页并打开登录弹框
                        return@Button
                    }

                    // 检查是否支付了升级管理员
                    isCreatingOrder = true
                    errorMessage = null

                    coroutineScope.launch {
                        try {
                            println("开始检查升级管理员支付状态: token=${token.take(10)}...") // 日志输出
                            val response = repository.checkUpgradeStatus(token)

                            if (response.isSuccess) {
                                val result = response.getOrNull()
                                if (result != null && (result["code"] as? Int) == 200) {
                                    val data = result["data"] as? Map<*, *>
                                    val isUpgraded = data?.get("isUpgraded") as? Boolean ?: false
                                    
                                    println("检查升级状态结果: isUpgraded=$isUpgraded") // 日志输出

                                    // 无论是否已支付升级，都跳转到资源信息页面
                                    println("跳转到资源信息页面，无论是否已支付升级") // 日志输出
                                    onShowResourceInfo(productId) // 调用回调，显示资源信息
                                } else {
                                    val message = result?.get("message") as? String ?: "未知错误"
                                    errorMessage = "检查升级状态失败: $message"
                                    println("检查升级状态失败: $errorMessage") // 日志输出
                                }
                            } else {
                                errorMessage = "请求失败：${response.exceptionOrNull()?.message}"
                                println("检查升级状态请求失败: $errorMessage") // 日志输出
                            }
                        } catch (e: Exception) {
                            errorMessage = "网络异常：${e.message}"
                            println("检查升级状态异常: ${e.message}") // 日志输出
                            e.printStackTrace()
                        } finally {
                            isCreatingOrder = false
                        }
                    }
                },
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight(),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF6200EA)), // 紫色
                enabled = !isCreatingOrder
            ) {
                if (isCreatingOrder) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            color = Color.White,
                            strokeWidth = 2.dp
                        )
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(text = "检查中...", fontSize = 14.sp, color = Color.White, fontWeight = FontWeight.Bold)
                    }
                } else {
                    Text(text = "资源", fontSize = 16.sp, color = Color.White, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}